import { useLocation } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import InfoHint from './InfoHint'
import { findMenuEntry } from '../app/navigation'
import { kit } from './strings'
import './PageHeader.css'

/**
 * The page identity card.
 *
 * The emblem defaults to the icon of the menu section the route belongs to, so
 * every page gets the mark for free and the rail, the header context line and
 * this card all agree on what the page IS. Pass `icon` to override it, or
 * `icon={null}` for a page that lives outside the menu and has no mark.
 *
 * `actions` and `scope` keep their old contracts, so a page that has not been
 * reworked yet still renders exactly as before — `scope` simply now docks into
 * the bottom of the card instead of floating below it.
 */
/**
 * @param {import('react').ReactNode} [hint]  guidance too long to sit on the page
 *   as a paragraph. It goes behind the "i" beside the title — read once, then in
 *   the way forever, which is exactly what an info hint is for.
 * @param {() => void} [onBack]  makes this a page you came to FROM somewhere: a
 *   details screen, a report about one row. The arrow sits before the mark, where
 *   the reading eye starts, and is the same control on every such page — see
 *   `useGoBack` for what it should do. A page reached from the menu has no origin
 *   and takes none.
 */
export default function PageHeader({
  title,
  subtitle,
  hint,
  count,
  actions,
  scope,
  icon,
  onBack,
  backLabel = kit.back,
}) {
  const { pathname } = useLocation()
  const Emblem = icon === undefined ? findMenuEntry(pathname)?.group.icon : icon

  return (
    <header className="page-header" data-testid="page-header">
      <div className="page-header__bar">
        <div className="page-header__identity">
          {onBack && (
            <button
              type="button"
              className="page-header__back"
              onClick={onBack}
              aria-label={backLabel}
              title={backLabel}
              data-testid="page-back"
            >
              <ArrowRight className="ltr-flip" size={18} strokeWidth={2} />
            </button>
          )}

          {Emblem && (
            <span className="page-header__emblem" aria-hidden="true">
              <Emblem size={22} strokeWidth={1.75} />
            </span>
          )}

          <div className="page-header__titles">
            <h1 className="page-header__title" data-testid="page-title">
              <bdi>{title}</bdi>
              {hint && <InfoHint content={hint} />}
            </h1>

            {/* The count is metadata about the list, so it reads on the meta line
                with the subtitle rather than as a badge competing with the title.
                It also drops its `سجل` unit: on a page whose title already says
                what is being counted, the word only diluted the figure. */}
            {(subtitle || count != null) && (
              <p className="page-header__meta">
                {subtitle && (
                  <span className="page-header__subtitle">
                    <bdi>{subtitle}</bdi>
                  </span>
                )}
                {subtitle && count != null && (
                  <span className="page-header__meta-dot" aria-hidden="true" />
                )}
                {count != null && (
                  <span className="page-header__count">{count.toLocaleString('en-US')}</span>
                )}
              </p>
            )}
          </div>
        </div>

        {actions && <div className="page-header__actions">{actions}</div>}
      </div>

      {scope && <div className="page-header__scope">{scope}</div>}
    </header>
  )
}
